import React, { useContext } from 'react'
import { HoverCard } from 'radix-ui'

import { cn } from '@/utlis/cn'
import { PortalContext } from './PortalContext'

type TooltipProps = {
    className?: string
    text?: React.ReactNode | null
    position?: 'top' | 'right' | 'bottom' | 'left'
    variant?: 'base' | 'info' | 'error'
    forceNative?: boolean
    noNativeWrapper?: boolean
    asChild?: boolean
}

const tooltipStyles = {
    base: [
        'bg-[rgba(58,58,58,1)]',
        'rounded-[16px]',
        'items-center',
        'border border-white/5',
        'shadow-glow',
        'p-[8px]',
        'z-39',
    ],
    info: ['bg-[rgba(56,51,28,1)]'],
    error: ['bg-red-900/80'],
}

const Tooltip = ({
    className,
    text,
    children,
    position = 'top',
    variant = 'base',
    forceNative,
    noNativeWrapper,
    asChild = true,
}: React.PropsWithChildren<TooltipProps>) => {
    const portalContainer = useContext(PortalContext)

    if (!text) {
        return <>{children}</>
    }

    if (forceNative) {
        const nativeText = getTextContent(text)
        if (noNativeWrapper && React.Children.count(children) === 1 && React.isValidElement(children)) {
            return React.cloneElement(children, {
                // @ts-ignore Set title prop regardless of the child type
                title: nativeText,
            })
        }

        return (
            <div className={cn('w-full h-full', className)} title={nativeText}>
                {children}
            </div>
        )
    }

    return (
        <HoverCard.Root openDelay={400} closeDelay={0}>
            <HoverCard.Trigger asChild={asChild} className={className}>
                {children}
            </HoverCard.Trigger>
            <HoverCard.Portal container={portalContainer}>
                <HoverCard.Content
                    side={position}
                    sideOffset={5}
                    className={cn(
                        tooltipStyles.base,
                        variant === 'info' ? tooltipStyles.info : '',
                        variant === 'error' ? tooltipStyles.error : ''
                    )}
                >
                    {text}
                    <HoverCard.Arrow className="fill-[rgba(58,58,58,1)] stroke-1 stroke-white/20" />
                </HoverCard.Content>
            </HoverCard.Portal>
        </HoverCard.Root>
    )
}

function getTextContent(node: React.ReactNode): string {
    if (node == null || typeof node === 'boolean') {
        return ''
    }

    if (typeof node === 'string' || typeof node === 'number') {
        return String(node)
    }

    if (React.isValidElement(node)) {
        return getTextContent(node.props.children)
    }

    if (Array.isArray(node)) {
        return node.map(getTextContent).filter(Boolean).join(' ')
    }

    // Handle React fragments
    if (typeof node === 'object' && 'props' in node && node.props?.children) {
        return getTextContent(node.props.children)
    }

    return ''
}

export default Tooltip
